// src/utils/ConfigService.ts

export interface AppConfig {
  REACT_APP_API_ENDPOINT: string;
  [key: string]: string;
}

class ConfigService {
  private config: AppConfig | null = null;
  private configLoadPromise: Promise<AppConfig> | null = null;

  /**
   * Loads configuration from /config.json.
   */
  loadConfig(): Promise<AppConfig> {
    if (this.configLoadPromise) {
      return this.configLoadPromise;
    }

    console.log("Fetching application configuration...");
    this.configLoadPromise = this.fetchConfig().then((loadedConfig) => {
      this.config = loadedConfig;
      console.log("Application configuration loaded successfully.");
      return loadedConfig;
    });

    return this.configLoadPromise;
  }

  private async fetchConfig(): Promise<AppConfig> {
    try {
      const timestamp = new Date().getTime();
      const response = await fetch(`/config.json?t=${timestamp}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const externalConfig: AppConfig = await response.json();
      console.log("Loaded external config:", externalConfig);

      if (!externalConfig.REACT_APP_API_ENDPOINT) {
        throw new Error("External config missing REACT_APP_API_ENDPOINT");
      }
      return externalConfig;
    } catch (error) {
      console.warn(
        "Failed to load external config, falling back to environment variables:",
        error
      );

      // Create config from all REACT_APP_ environment variables
      const envConfig: AppConfig = {
        REACT_APP_API_ENDPOINT: process.env.REACT_APP_API_ENDPOINT || "",
      };

      // Dynamically add all REACT_APP_* environment variables
      Object.keys(process.env).forEach((key) => {
        if (key.startsWith("REACT_APP_")) {
          envConfig[key] = process.env[key] || "";
        }
      });

      if (!envConfig.REACT_APP_API_ENDPOINT) {
        throw new Error(
          "FATAL: No API endpoint found in config.json or environment variables."
        );
      }
      console.log("Using environment config:", envConfig);
      return envConfig;
    }
  }

  /**
   * Generic method to get any config value by key
   */
  getConfigValue(key: string): string {
    if (!this.config) {
      throw new Error(
        `ConfigService Error: getConfigValue(${key}) called before config was loaded.`
      );
    }

    const value = this.config[key];
    if (value === undefined) {
      console.warn(`Config key '${key}' not found in configuration`);
      return "";
    }

    return value;
  }

  /**
   * Get all config values (useful for debugging)
   */
  getAllConfig(): AppConfig {
    if (!this.config) {
      throw new Error(
        "ConfigService Error: getAllConfig() called before config was loaded."
      );
    }
    return { ...this.config };
  }

  getApiEndpoint(): string {
    return this.getConfigValue("REACT_APP_API_ENDPOINT");
  }

  getProfileEndpoint(): string {
    return this.getConfigValue("REACT_APP_API_PROFILE_FRONTEND");
  }

  isConfigLoaded(): boolean {
    return this.config !== null;
  }
}

// Export a singleton instance
export const configService = new ConfigService();
